import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination';
import StatusBadge from '../../components/StatusBadge';
import api from '../../services/api';
import { LEAD_STATUS } from '../../utils/constants';
import { formatDateTime } from '../../utils/formatDate';

const LeadActivitiesPage = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [leads, setLeads] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    lead_id: '',
    status: ''
  });

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const response = await api.get('/leads', { params: { limit: 1000 } });
        const list = response.data.data?.leads || [];
        setLeads(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Failed to load leads:', error);
        setLeads([]);
      }
    };

    fetchLeads();
  }, []);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        const params = {
          page,
          limit: pageSize,
          ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value))
        };
        const response = await api.get('/leads/activities', { params });
        const data = response.data.data || {};
        setActivities(Array.isArray(data.activities) ? data.activities : []);
        setPagination(data.pagination || null);
      } catch (error) {
        console.error('Failed to load activities:', error);
        setActivities([]);
        setPagination(null);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [page, pageSize, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  return (
    <Layout>
      <div className="container-fluid">
        <div className="dashboard-header">
          <h1>Lead Activity</h1>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-sm-6">
                <label className="form-label small mb-1">Lead</label>
                <select
                  name="lead_id"
                  className="form-select"
                  value={filters.lead_id}
                  onChange={handleFilterChange}
                >
                  <option value="">All leads</option>
                  {leads.map((lead) => (
                    <option key={lead._id} value={lead._id}>
                      {lead.name}{lead.company ? ` (${lead.company})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-6">
                <label className="form-label small mb-1">Changed to</label>
                <select
                  name="status"
                  className="form-select"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">All statuses</option>
                  {Object.values(LEAD_STATUS).map((status) => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="card table-responsive">
          {loading ? (
            <Loading />
          ) : (
            <>
              <table className="table table-constrained mb-0 table-hover">
                <colgroup>
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '10%' }} />
                </colgroup>
                <thead className="table-light border-light">
                  <tr>
                    <th>S.No.</th>
                    <th>When</th>
                    <th>Lead</th>
                    <th>Company</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        No status changes found
                      </td>
                    </tr>
                  ) : (
                    activities.map((activity, index) => (
                      <tr key={`${activity.link}-${activity.changed_at}-${index}`} className="align-middle">
                        <td className="text-nowrap">{(page - 1) * pageSize + index + 1}</td>
                        <td className="cell-wrap">{formatDateTime(activity.changed_at)}</td>
                        <td className="fw-medium cell-wrap">{activity.name || 'N/A'}</td>
                        <td className="cell-wrap">{activity.company || 'N/A'}</td>
                        <td>{activity.from_status ? <StatusBadge status={activity.from_status} /> : <span className="text-muted">—</span>}</td>
                        <td>{activity.to_status ? <StatusBadge status={activity.to_status} /> : 'N/A'}</td>
                        <td>
                          {activity.link && (
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => navigate(activity.link)}
                            >
                              View
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="p-3">
                <Pagination
                  pagination={pagination}
                  onPageChange={setPage}
                  pageSize={pageSize}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default LeadActivitiesPage;
